import { GAME_CONFIG } from "../config/gameConfig.js";
import { getRoomAt, getRooms } from "../systems/rooms.js";
import { getLightsDebug } from "../systems/lighting.js";
import { isDebugModeVisible, setDebugModeVisible } from "../systems/debugState.js";
import { getPositionAnchorDebugCell } from "../systems/level.js";

/**
 * Debug overlay — world/tile coords + room id. Toggle with backtick (`).
 */

let hudRoot = null;
let label = null;
let lightGfx = [];
let roomGfx = [];
let enabled = true;

const COLLISION_BOUND_COLORS = {
    player: () => rgb(80, 255, 120),
    guard_robot: () => rgb(255, 70, 70),
};

function clearRoomGfx() {
    for (const g of roomGfx) {
        if (g.exists?.()) destroy(g);
    }
    roomGfx = [];
}

function clearLightGfx() {
    for (const g of lightGfx) {
        if (g.exists?.()) destroy(g);
    }
    lightGfx = [];
}

function drawLightGidLabels() {
    clearLightGfx();
    if (!enabled) return;

    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;

    for (const light of getLightsDebug()) {
        if (light.gid == null) continue;
        const tag = add([
            text(`L${light.gid}`, { font: "ShareTechMono", size: 10 }),
            pos(light.pos.x - tw * 0.25, light.pos.y - th * 0.6),
            color(255, 220, 80),
            opacity(0.95),
            z(8802),
            "debug_light_gid",
        ]);
        lightGfx.push(tag);
    }
}

function getCollisionBoundColor(obj) {
    if (obj.is?.("player")) return COLLISION_BOUND_COLORS.player();
    return COLLISION_BOUND_COLORS.guard_robot();
}

function shouldDrawCollisionBounds(obj) {
    if (!obj?.exists?.()) return false;
    if (!obj.has?.("area")) return false;
    if (obj.fixed) return false;
    return obj.is?.("player") || obj.is?.("guard_robot");
}

function drawCollisionBounds() {
    if (!enabled) return;
    if (GAME_CONFIG.debug?.collisionBounds === false) return;

    for (const obj of get("*", { recursive: true })) {
        if (!shouldDrawCollisionBounds(obj)) continue;

        const bbox = obj.worldBbox?.();
        if (!bbox || bbox.width <= 0 || bbox.height <= 0) continue;

        const boundColor = getCollisionBoundColor(obj);
        drawRect({
            pos: bbox.pos,
            width: bbox.width,
            height: bbox.height,
            fill: false,
            color: boundColor,
            outline: {
                width: 1,
                color: boundColor,
                opacity: 0.95,
            },
        });
    }
}

function drawRoomOutlines() {
    clearRoomGfx();
    if (!enabled) return;
    if (GAME_CONFIG.debug?.roomOutlines !== true) return;

    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;

    for (const room of getRooms()) {
        const r = room.tileRect;
        const box = add([
            rect(r.w * tw, r.h * th),
            pos(r.x * tw, r.y * th),
            anchor("topleft"),
            outline(1, rgb(0, 255, 200)),
            color(0, 255, 200),
            opacity(0.08),
            z(8800),
            "debug_room_outline",
        ]);
        const tag = add([
            text(`R${room.id}`, { font: "ShareTechMono", size: 10 }),
            pos(r.x * tw + 4, r.y * th + 4),
            color(0, 255, 200),
            opacity(0.85),
            z(8801),
            "debug_room_outline",
        ]);
        roomGfx.push(box, tag);
    }
}

export function isDebugHUDVisible() {
    return isDebugModeVisible();
}

export function initDebugHUD() {
    destroyAll("debug_hud");
    destroyAll("debug_room_outline");
    destroyAll("debug_light_gid");
    roomGfx = [];
    lightGfx = [];

    enabled = GAME_CONFIG.debug?.hud !== false;
    setDebugModeVisible(enabled);

    hudRoot = add([
        fixed(),
        z(9900),
        "debug_hud",
    ]);

    label = hudRoot.add([
        text("", { font: "ShareTechMono", size: 12 }),
        pos(12, height() - 52),
        color(180, 255, 160),
        opacity(0.95),
    ]);

    const hint = hudRoot.add([
        text("` debug  |  yellow L# = Light GID (edit lighting.lightByGid)", {
            font: "ShareTechMono",
            size: 10,
        }),
        pos(12, height() - 32),
        color(120, 160, 140),
        opacity(0.8),
    ]);

    onKeyPress("`", () => {
        enabled = !enabled;
        setDebugModeVisible(enabled);
        if (hudRoot) hudRoot.hidden = !enabled;
        if (enabled) {
            drawRoomOutlines();
            drawLightGidLabels();
        } else {
            clearRoomGfx();
            clearLightGfx();
        }
    });

    onUpdate(() => {
        if (!enabled || !label) return;

        label.pos = vec2(12, height() - 52);
        hint.pos = vec2(12, height() - 32);

        const player = get("player")[0];
        if (!player) {
            label.text = "pos: —";
            return;
        }

        const tw = GAME_CONFIG.level.tileWidth;
        const th = GAME_CONFIG.level.tileHeight;
        const tx = Math.floor(player.pos.x / tw);
        const ty = Math.floor(player.pos.y / th);
        const anchorCell = getPositionAnchorDebugCell();
        const rtx = tx - anchorCell.x;
        const rty = ty - anchorCell.y;
        const room = getRoomAt(player.pos);
        const roomStr = room ? `R${room.id}` : "none";

        const roomLights = getLightsDebug().filter(
            (l) => l.gid != null && l.roomId === room?.id,
        );
        const lightStr = roomLights.length
            ? roomLights.map((l) => `L${l.gid}`).join(",")
            : "none";

        label.text =
            `x:${player.pos.x.toFixed(0)} y:${player.pos.y.toFixed(0)}` +
            `  tile:${rtx},${rty}` +
            `  abs:${tx},${ty}` +
            `  room:${roomStr}` +
            `  lights:${lightStr}`;
    });

    onDraw(drawCollisionBounds);

    hudRoot.hidden = !enabled;
    if (enabled) {
        drawRoomOutlines();
        drawLightGidLabels();
    }
}
